import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Network, List } from "lucide-react";
import { CareerListView } from "@/components/CareerListView";

const W = 1240, H = 800, CX = 620, CY = 400, RX = 370, RY = 320;
const FILTERS = {
  public: ["Any", "High", "Moderate", "Low", "Minimal"],
  people: ["Any", "Many", "Some", "Few", "Self-determined"],
  setting: ["Any", "Mostly indoors", "Mixed indoor/outdoor", "Mostly outdoors"],
  remote: ["Any", "Remote-friendly", "Hybrid possible", "On-site"],
};
const FILTER_LABEL = { public: "Public contact", people: "People around", setting: "Setting", remote: "Remote work" };
const MAX_KIDS = 9;

const FilterRow = ({ k, value, onChange }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span className="text-xs font-mono-ui text-slate-500 mr-1 w-28 uppercase">{FILTER_LABEL[k]}</span>
    {FILTERS[k].map((o) => (
      <button key={o} data-testid={`mindmap-filter-${k}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => onChange(o)}
        className={`rounded-full px-3 py-1 text-xs border transition-colors ${value === o ? "bg-[#1C242B] text-white border-[#1C242B]" : "bg-white border-[#1C242B]/15 hover:border-[#1C242B]/40"}`}>
        {o}
      </button>
    ))}
  </div>
);

export const MindMap = ({ token, careers, clusters }) => {
  const byId = useMemo(() => Object.fromEntries(careers.map((c) => [c.id, c])), [careers]);
  const [open, setOpen] = useState(() => new Set([clusters[0].id]));
  const [sel, setSel] = useState(careers[0]?.id);
  const [f, setF] = useState({ public: "Any", people: "Any", setting: "Any", remote: "Any" });
  const [view, setView] = useState(() => (window.innerWidth < 768 ? "list" : "map"));
  const [focus, setFocus] = useState(null);
  const pass = (c) => Object.entries(f).every(([k, v]) => v === "Any" || c[k] === v);
  const order = useMemo(() => [...clusters].sort((a, b) => a.label.localeCompare(b.label)), [clusters]);

  const toggle = (id) => setOpen((s) => (s.has(id) ? new Set() : new Set([id])));
  const selected = byId[sel];

  return (
    <div data-testid="career-mindmap" className="rounded-2xl bg-white border border-[#1C242B]/10 overflow-hidden">
      <div className="p-5 sm:p-6 border-b border-[#1C242B]/10 flex flex-col lg:flex-row gap-5 lg:items-start lg:justify-between">
        <div className="grid gap-2.5">
          {Object.keys(FILTERS).map((k) => <FilterRow key={k} k={k} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} />)}
        </div>
        <div className="inline-flex self-start rounded-full bg-[#FAF8F5] border border-[#1C242B]/12 p-1" data-testid="mindmap-view-toggle">
          {[["map", "Map", Network], ["list", "List", List]].map(([k, l, I]) => (
            <button key={k} data-testid={`mindmap-view-${k}-btn`} onClick={() => setView(k)}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm transition-colors ${view === k ? "bg-[#1C242B] text-white" : "text-slate-600 hover:text-[#1C242B]"}`}>
              <I size={14} /> {l}
            </button>
          ))}
        </div>
      </div>
      {view === "list" ? <CareerListView token={token} clusters={clusters} byId={byId} pass={pass} focusId={focus} /> : (
      <div className="grid grid-cols-12">
        <div className="col-span-12 lg:col-span-8 bg-[#FAF8F5]/60">
          <div className="overflow-x-auto"><svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto min-w-[860px]" role="img" aria-label="Career mind map">
            {[...order.filter((c) => !open.has(c.id)), ...order.filter((c) => open.has(c.id))].map((cl) => {
              const i = order.indexOf(cl);
              const a = ((i + 0.5) / order.length) * Math.PI * 2 - Math.PI / 2;
              const x = CX + Math.cos(a) * RX, y = CY + Math.sin(a) * RY;
              const isOpen = open.has(cl.id);
              const side = x >= CX ? 1 : -1;
              const all = cl.careers.map((id) => byId[id]);
              const kids = all.slice(0, MAX_KIDS);
              const visible = all.filter(pass).length;
              const spread = Math.min(Math.PI * 0.9, kids.length * 0.32);
              return (
                <g key={cl.id} style={{ opacity: open.size && !isOpen ? 0.4 : 1, transition: "opacity 0.3s ease" }}>
                  <line className="mm-line" x1={CX} y1={CY} x2={x} y2={y} stroke={cl.color} strokeOpacity={0.25 + cl.score / 200} strokeWidth={1 + cl.score / 30} />
                  {isOpen && kids.map((c, j) => {
                    const kx = x + side * (40 + cl.score / 7), ky = y + (j - (kids.length - 1) / 2) * 23;
                    const ok = pass(c);
                    const rad = 4 + (c.score / 100) * 6;
                    return (
                      <g key={c.id} className="mm-node" opacity={ok ? 1 : 0.15} onClick={() => setSel(c.id)} data-testid={`mindmap-career-${c.id}`}>
                        <path d={`M${x},${y} Q${x + side * 20},${ky} ${kx},${ky}`} fill="none" stroke={cl.color} strokeOpacity={0.45} strokeWidth={1.2} />
                        <circle cx={kx} cy={ky} r={rad} fill={sel === c.id ? "#1C242B" : cl.color} stroke="#fff" strokeWidth={2} />
                        <text x={kx + side * (rad + 6)} y={ky + 4} textAnchor={side > 0 ? "start" : "end"} fontSize="13" fill="#1C242B" fontFamily="Figtree" fontWeight={sel === c.id ? 700 : 500}>
                          {c.title.length > 23 ? c.title.slice(0, 22) + "…" : c.title}
                          <tspan fontFamily="JetBrains Mono" fill="#C05630" fontSize="12">{` ${c.score}%`}</tspan>
                        </text>
                      </g>
                    );
                  })}
                  {isOpen && all.length > MAX_KIDS && (
                    <text x={x + side * (40 + cl.score / 7)} y={y + ((kids.length + 1) / 2) * 23 + 4} textAnchor={side > 0 ? "start" : "end"} fontSize="11.5" fill="#3E6B5C" fontFamily="Figtree"
                      className="mm-node" onClick={() => { setFocus(cl.id); setView("list"); }} data-testid={`mindmap-more-${cl.id}`}>{`+${all.length - MAX_KIDS} more in list view`}</text>
                  )}
                  <g className="mm-node" onClick={() => { toggle(cl.id); if (!isOpen) setSel(cl.careers[0]); }} data-testid={`mindmap-cluster-${cl.id}`}>
                    <circle cx={x} cy={y} r={14 + cl.score / 7} fill={isOpen ? cl.color : "#fff"} stroke={cl.color} strokeWidth={2.5} />
                    <text x={x} y={y + 4} textAnchor="middle" fontSize="12" fontWeight="700" fill={isOpen ? "#fff" : cl.color} fontFamily="JetBrains Mono">{cl.score}</text>
                    <text x={x + (isOpen ? -side : side) * (22 + cl.score / 7)} y={y + 1} textAnchor={(isOpen ? -side : side) > 0 ? "start" : "end"} fontSize="14" fontWeight="600" fill="#1C242B" fontFamily="Outfit">{cl.label}</text>
                    {!isOpen && <text x={x + side * (22 + cl.score / 7)} y={y + 16} textAnchor={side > 0 ? "start" : "end"} fontSize="10.5" fill="#64748B" fontFamily="Figtree">{`${visible} careers · tap to expand`}</text>}
                  </g>
                </g>
              );
            })}
            <circle cx={CX} cy={CY} r={44} fill="#1C242B" />
            <text x={CX} y={CY + 6} textAnchor="middle" fontSize="18" fontWeight="700" fill="#FAF8F5" fontFamily="Outfit">You</text>
          </svg></div>
          <p className="px-6 pb-4 text-xs text-slate-500">Tap a cluster to open its careers (one at a time). Tap a career to see why it fits. Node size and line weight reflect match strength. Filters dim careers that don't fit your criteria.</p>
        </div>
        <aside className="col-span-12 lg:col-span-4 border-t lg:border-t-0 lg:border-l border-[#1C242B]/10 p-6" data-testid="mindmap-detail-panel">
          {selected ? (
            <div className="fade-up" key={selected.id}>
              <p className="overline-label">{clusters.find((c) => c.id === selected.cluster)?.label}</p>
              <h3 className="font-display text-2xl font-bold mt-2">{selected.title}</h3>
              <p className="font-mono-ui text-3xl font-semibold text-[#D96B43] mt-3" data-testid="mindmap-selected-score">{selected.score}%</p>
              <p className="text-sm text-slate-600 mt-2">{selected.summary}</p>
              <div className="flex flex-wrap gap-1.5 mt-4 text-xs">
                <span className="rounded-full bg-[#E8F0EC] text-[#3E6B5C] px-2.5 py-1">Public: {selected.public}</span>
                <span className="rounded-full bg-[#E8F0EC] text-[#3E6B5C] px-2.5 py-1">People: {selected.people}</span>
                <span className="rounded-full bg-[#E8F0EC] text-[#3E6B5C] px-2.5 py-1">{selected.setting}</span>
                {selected.remote && <span className="rounded-full bg-[#F6E7DF] text-[#C05630] px-2.5 py-1">{selected.remote}</span>}
              </div>
              <p className="text-xs font-mono-ui text-slate-500 mt-5">WHY IT ALIGNS</p>
              <ul className="mt-2 space-y-2 text-sm text-slate-700">
                {selected.reasons.map((r) => <li key={r} className="pl-3 border-l-2 border-[#D96B43]/50">{r}</li>)}
              </ul>
              <Link to={`/results/${token}/career/${selected.id}`} data-testid="mindmap-view-career-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1C242B] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#D96B43] transition-colors">
                Full details &amp; next steps <ArrowUpRight size={16} />
              </Link>
            </div>
          ) : <p className="text-sm text-slate-500">Select a career node.</p>}
        </aside>
      </div>
      )}
    </div>
  );
};
